/**
 * Orchestration de la page Détail/Modification d'un exercice :
 * paramètres de route, chargement, mode édition, saisie et enregistrement.
 */

import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import { useExerciseLoader } from './useExerciseLoader';
import { useTagListEditors } from './useTagListEditors';
import { useExerciseSave } from './useExerciseSave';
import { ExerciseFieldChange } from './types';

/** Regroupe états et handlers de la page exercice (lecture, édition, création). */
export const useBookExerciseView = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNewMode = id === 'new';

  // Pour création, lessonId vient des query params
  const lessonIdFromParams = searchParams.get('lessonId');

  const { environment: env } = useEnvironment();
  const loader = useExerciseLoader(id, env, isNewMode, lessonIdFromParams);
  const { exercise, setExercise, formData, setFormData, setError } = loader;
  const [isEditMode, setIsEditMode] = useState(isNewMode);
  const tags = useTagListEditors(formData, setFormData);
  const { saving, handleSave } = useExerciseSave({
    id, env, isNewMode, formData, exercise, setExercise, setIsEditMode, setError, navigate,
  });

  // Handlers
  const handleChange: ExerciseFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (isNewMode) {
      navigate('/book-exercises');
    } else {
      if (exercise) {
        setFormData(exercise);
      }
      setIsEditMode(false);
      setError('');
    }
  };

  const goToList = () => navigate('/book-exercises');

  return {
    ...loader, isNewMode, lessonIdFromParams, isEditMode, saving, tags,
    handleChange, handleEdit, handleCancel, handleSave, goToList,
  };
};
